import React from "react";

import ScorePlusFilters from "./ScorePlusFilters";
import ScorePlusList from "./ScorePlusList";
import useScorePlus from "./useScorePlus";
import ScorePlusForm from "./ScorePlusForm";
import ScorePlusToolbar from "./ScorePlusToolbar";
import ScorePlusMessage from "./ScorePlusMessage";
import ScorePlusDashboard from "./ScorePlusDashboard";
import ScorePlusStatistics from "./ScorePlusStatistics";
import ScorePlusInsights from "./ScorePlusInsights";

import ScorePlusDailyTrend from "./charts/ScorePlusDailyTrend";
import ScorePlusActivityChart from "./charts/ScorePlusActivityChart";
import ScorePlusStatusChart from "./charts/ScorePlusStatusChart";
import ScorePlusHeatMap from "./charts/ScorePlusHeatMap";

import ScorePlusCalendar from "./calendar/ScorePlusCalendar";
import ScorePlusAbout from "./ScorePlusAbout";
import ScorePlusSubscriptionActivity from "./ScorePlusSubscriptionActivity";

const ScorePlus = (props) => {
  const scorePlus = useScorePlus(props);

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-3xl border border-purple-500/20 bg-gradient-to-br from-slate-950 via-purple-950/40 to-slate-900 px-5 py-7 shadow-2xl shadow-purple-950/20 sm:px-8 sm:py-9">
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-3xl">
            <h1 className="text-left text-xl lg:text-2xl font-black tracking-tight text-cyan-200">
              💜 ScorePlus
            </h1>

            <p className="mt-2 max-w-2xl text-xs lg:text-sm leading-6 text-slate-300">
              Your life is generating valuable data every day. Yet most of it
              gets lost in scattered notes, reports and memories.
              <span className="font-semibold text-purple-200">
                {" "}
                Don't lose the trail.
              </span>
            </p>
          </div>
        </div>

        <div className="relative mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/5 pt-2">
          {[
            { icon: "fa-heart-pulse", label: "Life & Health" },
            { icon: "fa-user-shield", label: "Personal Data Companion" },
            { icon: "fa-calendar-check", label: "Daily Records" },
            { icon: "fa-layer-group", label: "Structured Data" },
            { icon: "fa-chart-line", label: "Track Progress" },
            { icon: "fa-microchip", label: "Your AI, Not GenAI" },
          ].map((item, index) => (
            <React.Fragment key={item.label}>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                <i
                  className={`fa-solid ${item.icon} text-sm text-purple-300`}
                />
                {item.label}
              </span>

              {index < 5 && (
                <span className="hidden h-3 w-px bg-white/10 sm:block" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <ScorePlusToolbar {...scorePlus} />

      <ScorePlusMessage
        message={scorePlus.message}
        clearMessage={scorePlus.clearMessage}
      />

      {scorePlus.activeView !== "form" &&
        scorePlus.activeView !== "subsactivitylink" &&
        scorePlus.activeView !== "about" &&
        scorePlus.filteredRecords.length > 0 && (
          <ScorePlusFilters
            filters={scorePlus.filters}
            updateFilters={scorePlus.updateFilters}
            recordsCount={scorePlus.filteredRecords.length}
            onClearFilters={scorePlus.clearFilters}
          />
        )}

      {scorePlus.activeView === "dashboard" && (
        <>
          <ScorePlusDashboard records={scorePlus.filteredRecords} />
          <ScorePlusStatistics records={scorePlus.filteredRecords} />
        </>
      )}

      {scorePlus.activeView === "calendar" && (
        <ScorePlusCalendar records={scorePlus.filteredRecords} />
      )}

      {scorePlus.activeView === "analytics" && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <ScorePlusDailyTrend records={scorePlus.filteredRecords} />
            <ScorePlusActivityChart records={scorePlus.filteredRecords} />
            <ScorePlusStatusChart records={scorePlus.filteredRecords} />
          </div>
          <ScorePlusInsights records={scorePlus.filteredRecords} />
        </>
      )}
      {scorePlus.activeView === "form" &&
        (scorePlus.sampleMode ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-10 text-center">
            <div className="text-5xl mb-3">🧪</div>

            <h2 className="text-lg font-bold text-amber-300">
              Sample Data Mode
            </h2>

            <p className="mt-2 text-sm text-zinc-300">
              Add, Edit and Delete are disabled while viewing Sample Data.
            </p>

            <p className="mt-1 text-xs text-zinc-500">
              Switch to{" "}
              <span className="font-semibold text-cyan-400">Live Mode</span> to
              manage your activities.
            </p>
          </div>
        ) : (
          <ScorePlusForm
            form={scorePlus.form}
            updateForm={scorePlus.updateForm}
            onSubmit={scorePlus.saveRecord}
            onCancel={scorePlus.resetForm}
          />
        ))}

      {scorePlus.activeView === "records" && (
        <ScorePlusList
          records={scorePlus.filteredRecords}
          onEdit={scorePlus.editRecord}
          onDelete={scorePlus.deleteRecord}
          sampleMode={scorePlus.sampleMode}
          onClearFilters={scorePlus.clearFilters}
          confirmDelete={scorePlus.confirmDelete}
          setConfirmDelete={scorePlus.setConfirmDelete}
        />
      )}

      {scorePlus.activeView === "subsactivitylink" && (
        <ScorePlusSubscriptionActivity />
      )}

      {scorePlus.activeView === "about" && <ScorePlusAbout />}
    </div>
  );
};

export default ScorePlus;
